/**
 * Feed slice — Discover's personalised DEFAULT view (docs/DISCOVERY.md §5.4–5.6).
 *
 * Discover now has three modes, and this slice owns the one you land on:
 *
 *   FEED     nothing typed, no tag picked. A ranked, paged page of the games
 *            table from `discover_page`: the member's Steam library plus Steam's
 *            live top 100, weighted by taste, owned games captioned and never
 *            hidden.
 *   BROWSE   a term or a tag — Steam's own search. `slices/browse.ts`.
 *   FALLBACK the feed could not be reached. Phase 1's client-side path over the
 *            local games table, AND THE SCREEN SAYS SO.
 *
 * That third mode is the whole reason this slice carries as much state as it
 * does. §5.6 is explicit: "a silent fallback is how a broken Phase 2 would look
 * identical to a working one". `feedFallback` exists so the screen can tell the
 * truth about which of the three it is showing.
 *
 * WHY THE ROWS DO NOT LAND IN `s.live`, unlike browse rows. A browse row is a
 * thin live-search row that may not have a games-table row at all. A feed row IS
 * a games-table row, fully shaped, and `discover_page` returns every column
 * `v_games` exposes. Merging them into `live` would make `browsable()` — which
 * exists to keep provisional rows out of the grid — start re-filtering rows the
 * server already decided about, server-side, with data the client does not have.
 */

import type { StateCreator } from 'zustand';

import { DISCOVER_LIMIT } from '../../lib/logic.ts';
import type { Game } from '../../lib/types.ts';
import type { DiscoverReason } from '../../repo/Repository.ts';
import type { QdStore } from '../types.ts';
import { browseMode, xtagSlugs } from './browse.ts';

export interface FeedSlice {
  /**
   * The shuffle seed, generated ONCE per session and sent with every page.
   *
   * It participates in the server's sort. A sampled feed whose order is not
   * reproducible produces duplicates and gaps across pages (§5.5) — the seed is
   * what makes page 2 continue page 1 rather than re-sample. A new session gets
   * a new seed, which is what stops the feed feeling like a fixed list.
   */
  feedSeed: number;
  feedRows: Game[];
  /** The TRUE total, over the whole candidate set rather than what is loaded. */
  feedTotal: number;
  /** What the filters removed, likewise over the whole set. */
  feedHidden: number;
  /** How many rows the feed could draw from at all. 0 is a different screen. */
  feedPoolN: number;
  feedCursor: string | null;
  feedState: '' | 'busy' | 'ok' | 'err';
  feedReason: DiscoverReason;
  feedPersonalized: boolean;
  /**
   * The feed could not be reached and Discover is on the Phase 1 path. Never set
   * without the screen saying so.
   */
  feedFallback: boolean;

  /** Throw away the current feed and ask for page 1 of the question as it is now. */
  restartFeed: () => void;
  /** Fetch page 1, or append the next page with `{ more: true }`. */
  fetchFeed: (opts?: { more?: boolean }) => Promise<void>;
}

/** Is Discover showing the ranked feed? One definition, like `browseMode`. */
export function feedMode(s: Pick<QdStore, 'q' | 'bTagsBy' | 'feedFallback'>): boolean {
  return !browseMode(s) && !s.feedFallback;
}

/**
 * The identity of the current feed question, for the stale-response guard.
 *
 * Sort and filters are in it because BOTH now run server-side: a page fetched
 * under one filter set must not be appended under another, or `total` describes
 * a set the rows do not come from — the §5.5 failure, arriving through a race
 * rather than through a design mistake. The excluded tags are in it for the
 * same reason and by the same route: they ride to `discover_page` as `xtags`,
 * so they are part of the question. Sorted, because a set's identity has no
 * order and `[3,7]` / `[7,3]` are the same question.
 */
function feedKey(s: Pick<QdStore, 'sort' | 'fltBy' | 'xTagsBy'>): string {
  // Discover's own set, named rather than inferred: this identifies a page that
  // may land after the user has navigated away (`store/filterKey.ts`).
  return (
    s.sort +
    '|' +
    JSON.stringify(s.fltBy.discover) +
    '|' +
    [...s.xTagsBy.discover].sort((a, b) => a - b).join(',')
  );
}

/**
 * Filters and sort now run SERVER-side, so every change to either is a new
 * request — and a price slider emits one per pixel. This is the same debounce
 * the browse slice applies to typing, for the same reason, and it is why the
 * filter setters can each call `restartFeed()` unconditionally without anyone
 * having to reason about which of them fire in bursts.
 */
export const FEED_DEBOUNCE_MS = 250;

export const createFeedSlice: StateCreator<QdStore, [], [], FeedSlice> = (set, get) => {
  /** Per-store, not per-module: two stores in one process must not share it. */
  let debT: ReturnType<typeof setTimeout> | null = null;

  return {
  // Session-stable by construction: evaluated once, when the store is built.
  feedSeed: Math.floor(Math.random() * 2147483647),
  feedRows: [],
  feedTotal: 0,
  feedHidden: 0,
  feedPoolN: 0,
  feedCursor: null,
  feedState: '',
  feedReason: 'ok',
  feedPersonalized: false,
  feedFallback: false,

  restartFeed: () => {
    if (debT) clearTimeout(debT);
    // Browse mode owns the screen; asking for a feed page now would fetch an
    // answer nothing will render and then race the browse response.
    if (browseMode(get())) return;
    set({
      feedRows: [],
      feedCursor: null,
      feedTotal: 0,
      feedHidden: 0,
      feedState: 'busy',
      // `feedFallback` is deliberately NOT cleared here: a fallback that cleared
      // itself on every filter change would flicker between two different
      // explanations of the same screen. `fetchFeed` clears it on a response
      // that actually arrived.
    });
    debT = setTimeout(() => {
      void get().fetchFeed();
    }, FEED_DEBOUNCE_MS);
  },

  fetchFeed: async (opts) => {
    const more = opts?.more === true;
    const s0 = get();
    if (browseMode(s0)) return;
    // LOAD MORE past the last page is a no-op, not an empty re-fetch of page 1.
    if (more && s0.feedCursor === null) return;

    const keyAtDispatch = feedKey(s0);
    set({ feedState: 'busy' });

    const page = await s0.repo.fetchDiscoverPage({
      q: '',
      seed: s0.feedSeed,
      limit: DISCOVER_LIMIT,
      cursor: more ? s0.feedCursor : null,
      sort: s0.sort,
      flt: s0.fltBy.discover,
      excludes: xtagSlugs(s0),
    });

    // The question changed while this was in the air. Applying it now would put
    // one filter set's rows under another filter set's count.
    if (feedKey(get()) !== keyAtDispatch || browseMode(get())) return;

    // FAILURE IS "NOT FROM THE NETWORK **AND** EMPTY", the same test the browse
    // slice applies, and the distinction is load-bearing. `fromNetwork: false`
    // means "this did not come off the wire" — which is also true of every
    // answer LocalRepository gives in development and in the store tests. Read
    // alone it would put the dev build permanently in the fallback, showing
    // "RANKING IS OFFLINE" over a feed that was working exactly as designed.
    if (!page.fromNetwork && page.rows.length === 0) {
      // The ONE state §5.6 refuses to let pass silently.
      set({ feedState: 'err', feedFallback: true });
      get().freshFail('UPSTREAM_STATUS');
      return;
    }

    set((s) => {
      // Dedupe by appid. The server's keyset makes duplicates unlikely rather
      // than impossible — a row whose score changed between pages can straddle
      // the cursor — and a duplicate React key is a real bug, not a cosmetic one.
      const seen = new Set(more ? s.feedRows.map((g) => g.appid) : []);
      const fresh = page.rows.filter((g) => !seen.has(g.appid));
      return {
        feedRows: more ? [...s.feedRows, ...fresh] : page.rows,
        feedTotal: page.total,
        feedHidden: page.hiddenByFilters,
        feedPoolN: page.poolN,
        feedCursor: page.nextCursor,
        feedReason: page.reason,
        feedPersonalized: page.personalized,
        feedState: 'ok',
        feedFallback: false,
        // Art arrived WITH the rows, so no tile has to re-fetch it.
        assets: { ...s.assets, ...page.assets },
      };
    });
    // Only a real round trip may refresh the chip — a fixture-backed answer is
    // right about the data and would be lying about the freshness.
    if (page.fromNetwork) get().freshOk();
  },
  };
};
